import { BaseFeature } from '../../core/base-feature.js';

/**
 * WordSearchFeature - Frontend feature for Word Search punishment
 * Allows admin to configure and trigger word search sessions on desktop,
 * see answer key, track cursor, and trigger evil features.
 */
export class WordSearchFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.sessionState = {
            isActive: false,
            gridSize: 20,
            totalWords: 0,
            foundCount: 0,
            words: [],
            foundWords: [],
            startedAt: null,
            completedAt: null,
            status: 'idle'
        };

        // Admin-only data (answer key)
        this.grid = null;
        this.placements = [];
        this.evilFeatures = {};
        this.cursorPosition = null;

        // Persisted config from desktop
        this.savedWords = [];
        this.savedGridSize = 20;

        // Event callbacks for React components
        this.stateUpdateCallbacks = new Set();
        this.completionCallbacks = new Set();
        this.updateCallbacks = new Set();
        this.cursorCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'word-search',
            name: 'Word Search',
            version: 'v1.0',
            tier: 'external',
            capabilities: [
                'start',
                'cancel',
                'get_state',
                'shuffle',
                'add_word',
                'set_grid_size',
                'toggle_evil',
                'letter_swap',
                'scramble_found',
                'decoy_flash'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[WordSearch] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                return this.handleStateUpdate(payload);
            case 'initial_state':
                return this.handleInitialState(payload);
            case 'get_state_response':
                return this.handleInitialState({
                    state: payload?.state || payload,
                    grid: payload?.state?.grid || null,
                    placements: payload?.state?.placements || null,
                    evilFeatures: payload?.state?.evilFeatures || null,
                    savedWords: payload?.state?.savedWords || [],
                    savedGridSize: payload?.state?.savedGridSize || 20
                });
            default:
                console.warn(`[WordSearch] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleStateUpdate(payload) {
        switch (payload.type) {
            case 'session_started':
                this.sessionState = {
                    isActive: true,
                    gridSize: payload.gridSize,
                    totalWords: payload.totalWords,
                    foundCount: 0,
                    words: payload.words || [],
                    foundWords: [],
                    startedAt: payload.startedAt,
                    completedAt: null,
                    status: 'active',
                    sessionId: payload.sessionId
                };
                this.grid = payload.grid;
                this.placements = payload.placements;
                this.evilFeatures = payload.evilFeatures || {};
                break;

            case 'word_found':
                this.sessionState.foundCount = payload.foundCount;
                if (payload.word && !payload.unfound) {
                    if (!this.sessionState.foundWords.includes(payload.word)) {
                        this.sessionState.foundWords.push(payload.word);
                    }
                }
                if (payload.unfound && payload.word) {
                    this.sessionState.foundWords = this.sessionState.foundWords.filter(w => w !== payload.word);
                }
                break;

            case 'session_completed':
                this.sessionState = {
                    ...this.sessionState,
                    isActive: false,
                    status: 'completed',
                    completedAt: payload.completedAt
                };
                this.notifyCompletionListeners(payload);
                break;

            case 'session_cancelled':
                this.sessionState = {
                    ...this.sessionState,
                    isActive: false,
                    status: 'cancelled',
                    completedAt: payload.completedAt
                };
                this.notifyCompletionListeners(payload);
                break;

            case 'grid_shuffled':
            case 'word_added':
                if (payload.grid) this.grid = payload.grid;
                if (payload.placements) this.placements = payload.placements;
                if (payload.gridSize) this.sessionState.gridSize = payload.gridSize;
                if (payload.totalWords) this.sessionState.totalWords = payload.totalWords;
                if (payload.words) this.sessionState.words = payload.words;
                if (payload.foundCount !== undefined) this.sessionState.foundCount = payload.foundCount;
                if (payload.foundWords) this.sessionState.foundWords = payload.foundWords;
                break;

            case 'evil_toggled':
                this.evilFeatures = payload.evilFeatures || this.evilFeatures;
                break;

            case 'cursor_activity':
                this.cursorPosition = payload;
                for (const cb of this.cursorCallbacks) {
                    try { cb(payload); } catch (e) { /* noop */ }
                }
                return; // Don't trigger generic update for cursor
        }

        this.notifyStateUpdateListeners(payload);
        this.notifyUpdateListeners();
    }

    handleInitialState(payload) {
        // Restore saved config (persisted word list + grid size)
        if (payload.savedWords) this.savedWords = payload.savedWords;
        if (payload.savedGridSize) this.savedGridSize = payload.savedGridSize;

        if (payload.state) {
            // Strip grid/placements/evilFeatures from state object (they're top-level)
            const { grid, placements, evilFeatures, savedWords, savedGridSize, ...sessionState } = payload.state;
            this.sessionState = sessionState;
        }

        // Restore active session data
        if (payload.grid) this.grid = payload.grid;
        if (payload.placements) this.placements = payload.placements;
        if (payload.evilFeatures) this.evilFeatures = payload.evilFeatures;

        this.notifyUpdateListeners();
    }

    // =====================================
    // Command Methods
    // =====================================

    async start(config) {
        this.sendMessage('start', config);
    }

    async cancel() {
        this.sendMessage('cancel', {});
    }

    async shuffle() {
        this.sendMessage('shuffle', {});
    }

    async setGridSize(size) {
        this.sendMessage('set_grid_size', { size });
    }

    async addWord(word) {
        this.sendMessage('add_word', { word });
    }

    async toggleEvil(feature, value, extraConfig) {
        this.sendMessage('toggle_evil', { feature, value, extraConfig });
    }

    async letterSwap(count) {
        this.sendMessage('letter_swap', { count });
    }

    async scrambleFound(word) {
        this.sendMessage('scramble_found', { word });
    }

    async decoyFlash() {
        this.sendMessage('decoy_flash', {});
    }

    async saveConfig(words, gridSize) {
        this.savedWords = words;
        this.savedGridSize = gridSize;
        this.sendMessage('save_config', { words, gridSize });
    }

    async getSessionState() {
        this.sendMessage('get_state', {});
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onStateUpdate(callback) {
        this.stateUpdateCallbacks.add(callback);
        return () => this.stateUpdateCallbacks.delete(callback);
    }

    onCompletion(callback) {
        this.completionCallbacks.add(callback);
        return () => this.completionCallbacks.delete(callback);
    }

    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    onCursorUpdate(callback) {
        this.cursorCallbacks.add(callback);
        return () => this.cursorCallbacks.delete(callback);
    }

    notifyStateUpdateListeners(data) {
        for (const cb of this.stateUpdateCallbacks) {
            try { cb(data); } catch (e) { console.error('[WordSearch] State update callback error:', e); }
        }
    }

    notifyCompletionListeners(data) {
        for (const cb of this.completionCallbacks) {
            try { cb(data); } catch (e) { console.error('[WordSearch] Completion callback error:', e); }
        }
    }

    notifyUpdateListeners() {
        const data = {
            state: this.sessionState,
            grid: this.grid,
            placements: this.placements,
            evilFeatures: this.evilFeatures,
            savedWords: this.savedWords,
            savedGridSize: this.savedGridSize
        };
        for (const cb of this.updateCallbacks) {
            try { cb(data); } catch (e) { console.error('[WordSearch] Update callback error:', e); }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getState() { return this.sessionState; }
    getGrid() { return this.grid; }
    getPlacements() { return this.placements; }
    getEvilFeatures() { return this.evilFeatures; }
    getCursor() { return this.cursorPosition; }
    isActive() { return this.sessionState.isActive; }
    getSavedWords() { return this.savedWords; }
    getSavedGridSize() { return this.savedGridSize; }

    async cleanup() {
        console.log('[WordSearch] Cleaning up...');
        this.sessionState = { isActive: false, gridSize: 20, totalWords: 0, foundCount: 0, words: [], foundWords: [], startedAt: null, completedAt: null, status: 'idle' };
        this.grid = null;
        this.placements = [];
        this.evilFeatures = {};
        this.cursorPosition = null;
        this.stateUpdateCallbacks.clear();
        this.completionCallbacks.clear();
        this.updateCallbacks.clear();
        this.cursorCallbacks.clear();
        this._pendingRequests.clear();
    }
}
